/**
 * Data layer for the COMPANY panel's Home Testing section.
 *
 * The source is the Vitall partner's kit order. What makes it worth a section
 * of its own is that the order carries two timestamps, and they split the wait
 * into two legs with completely different owners:
 *
 *  - ORDERED → RECEIVED is the person posting the kit back. A kit that never
 *    comes back is sitting in a drawer, and that is the one number a company
 *    that bought kits can actually act on.
 *  - RECEIVED → RESULTS is the laboratory. Nothing the company can change, but
 *    separating it out stops lab time from being blamed on take-up.
 *
 * Stages are derived from those timestamps rather than from `status.code`,
 * which is whatever the partner happens to send; the raw status codes are
 * reported alongside as their own distribution so a new one shows up as a new
 * row instead of vanishing.
 *
 * The biomarker results, the report files, the clinical comments and the
 * partner's copy of the person's date of birth, phone and address are never
 * fetched by any endpoint below, so they cannot reach this layer.
 *
 * Backed by the company/v1 home-testing-hub endpoints (token-scoped to the
 * company; the B2B/Members toggle is appended by api/companyApi.js). Every
 * fetcher takes an optional `department` so the section's filter can narrow the
 * whole page.
 *
 * The adapter is exported so a super-admin Home Testing hub, served by the same
 * builders, can reuse the field mapping rather than restating it.
 */

import {
  getCompanyHomeTestingDashboard,
  getCompanyHomeTestingUsers,
  getCompanyHomeTestingUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Kit-order block shared by every Home Testing dashboard. */
export const adaptHomeTestingOrders = (data) => ({
  // Reach and volume
  orders: data.orders ?? 0,
  users: data.users ?? 0,
  avgOrdersPerUser: data.avgOrdersPerUser ?? 0,
  firstOrderDate: data.firstOrderDate || null,
  lastOrderDate: data.lastOrderDate || null,

  // Funnel — all three stages from timestamps, so they always reconcile
  received: data.received ?? 0,
  withResults: data.withResults ?? 0,
  awaitingReturn: data.awaitingReturn ?? 0,
  awaitingResults: data.awaitingResults ?? 0,
  returnRate: data.returnRate ?? 0,
  resultsRate: data.resultsRate ?? 0,
  outstandingKits: data.outstandingKits ?? 0,

  // Timing, split by who owns the wait
  returnTimeSplit: data.returnTimeSplit || [],
  labTimeSplit: data.labTimeSplit || [],

  volumeSplit: data.volumeSplit || [],

  // Whatever the partner is sending, ranked by volume.
  statusBreakdown: data.statusBreakdown || [],

  // Each entry is { label, orders, users, withResults, resultsRate }.
  byKit: data.byKit || [],

  // Shortlisted but not ordered — demand an order table cannot show.
  wishlist: data.wishlist || [],

  weekdayOrders: data.weekdayOrders || [],

  // Backend zero-fills the last 30 days; charts consume plain series.
  ordersTrend: (data.dailyTrend || []).map((day) => day.orders || 0),
  peopleTrend: (data.dailyTrend || []).map((day) => day.users || 0)
});

export const fetchCompanyHomeTestingDashboard = async (department = '') => {
  try {
    const data = unwrap(await getCompanyHomeTestingDashboard(department));
    return {
      ...adaptHomeTestingOrders(data),
      // Company-only: the size of the population, so the page can say
      // "18 of 240 employees have ordered a kit".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyHomeTestingDashboard error:', error);
    return null;
  }
};

// Matches the HomeTestingUsersTable fetcher contract.
export const fetchCompanyHomeTestingUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyHomeTestingUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyHomeTestingUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's kit profile ({ user, statusBreakdown, topKits,
 * orderHistory }). The history rows carry the kit name, the dates and the
 * stage — never a result. The backend 404s any user outside this company, so no
 * scoping is needed here.
 */
export const fetchCompanyHomeTestingUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyHomeTestingUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyHomeTestingUserDetail error:', error);
    return null;
  }
};
